/**
 * Created by neo on 18.01.17.
 */

import { makeAutoObservable, observable, ObservableMap } from 'mobx';
import { CoachExerciseBlock } from './CoachExerciseBlock';
import dayjs from '../../../../Utils/dayjs';
import {
  BaseTrackingKey,
  PlanableTrackingKey,
  TrackingKey,
  TrackingKeysList,
} from '../../../ProgramPortfolio/TrackingKeys';

export const BasicTrackingKeys: BaseTrackingKey[] = ['REPETITIONS', 'DURATION', 'WEIGHT', 'BREAK', 'CALORIES'];

export type CoachExerciseBlockSet = {
  values: ObservableMap<TrackingKey, number>;
  previousValues: ObservableMap<TrackingKey, number>;
  exerciseBlock?: CoachExerciseBlock;
  readonly plannedCalories: number;
  readonly calories: number;
  readonly duration: number;
  readonly remainingDuration: number;
  readonly estimatedRepsDuration: number;
  readonly plannedRepetitions: number | undefined;
  readonly plannedWeight: number | undefined;
  readonly plannedRepetitionBasedDuration: number;
  readonly durationFormatted: string;
  readonly durationSeconds: number;
  readonly breakTime: number;
  readonly breakTimeMs: number;
  readonly plannedBreakTime: number;
  readonly plannedBreakTimeMs: number;
  readonly plannedDuration: number;
  readonly totalDuration: number;
  readonly plannedDurationMs: number;
  readonly tons: number;
  readonly index: number;
  readonly isLastSet: boolean;
  readonly prevSet: CoachExerciseBlockSet | undefined;
  readonly nextSet: CoachExerciseBlockSet | undefined;
  readonly assignedTrackingKeys: BaseTrackingKey[];
  readonly assignedTrackingKeysSorted: BaseTrackingKey[];
  readonly unassignedTrackingKeys: BaseTrackingKey[];
  readonly unassignedTrackingKeysSorted: BaseTrackingKey[];
  readonly allTrackingKeysSorted: BaseTrackingKey[];
  readonly unassignedTrackingKeysSortedWithRepetitionsPreference: BaseTrackingKey[];
  readonly unassignedTrackingKeysSortedWithDurationPreference: BaseTrackingKey[];
  readonly assignedTrackingKeysSortedWithRepetitionsPreference: BaseTrackingKey[];
  readonly assignedTrackingKeysSortedWithDurationPreference: BaseTrackingKey[];
  calculateCalories(bmr24: number): number;
  setKey(key: TrackingKey, value?: number): void;
  assignTrackingKey(trackingKey: string, value?: number): void;
  deleteTrackingKey(trackingKey: string): void;
  toWorkoutJS(): any;
};

export function createCoachExerciseBlockSet(exerciseBlock?: CoachExerciseBlock, json?: any): CoachExerciseBlockSet {
  return makeAutoObservable({
    values: observable.map(json?.values || {}),
    previousValues: observable.map(json?.previousValues || {}),
    exerciseBlock,

    get plannedCalories(): number {
      return this.values.get('CALORIES') || this.values.get('MIN_CALORIES') || this.values.get('MAX_CALORIES') || 0;
    },

    get calories(): number {
      return this.plannedCalories;
    },

    get duration(): number {
      return this.plannedDurationMs;
    },

    get remainingDuration(): number {
      return this.duration;
    },

    get estimatedRepsDuration(): number {
      switch (this.exerciseBlock?.exercise.type) {
        case 'MOBILISATION':
          return 1.5;
        case 'STRENGTH':
          return this.exerciseBlock?.phase?.type === 'interval'
            ? 1.5
            : this.exerciseBlock?.exercise.equipmentTypes.includes('FREE')
            ? 2
            : 3;
        default:
          return 3;
      }
    },

    get plannedRepetitions(): number | undefined {
      return this.values.get('MAX_REPETITIONS') || this.values.get('MIN_REPETITIONS') || this.values.get('REPETITIONS');
    },

    get plannedWeight(): number | undefined {
      return this.values.get('MAX_WEIGHT') || this.values.get('MIN_WEIGHT') || this.values.get('WEIGHT');
    },

    get plannedRepetitionBasedDuration(): number {
      return (this.plannedRepetitions || 0) * this.estimatedRepsDuration;
    },

    get durationFormatted(): string {
      const { duration } = this;
      if (duration > 0) {
        return duration > 3599999 ? dayjs.utc(duration).format('HH:mm:ss') : dayjs.utc(duration).format('mm:ss');
      }
      return '00:00';
    },

    get durationSeconds(): number {
      return this.plannedDuration;
    },

    get breakTime(): number {
      return this.plannedBreakTime;
    },

    get breakTimeMs(): number {
      return this.plannedBreakTime * 1000;
    },

    get plannedBreakTime(): number {
      return this.values.get('MIN_BREAK') || this.values.get('MAX_BREAK') || this.values.get('BREAK') || 10;
    },

    get plannedBreakTimeMs(): number {
      return this.plannedBreakTime * 1000;
    },

    get plannedDuration(): number {
      return (
        this.values.get('MIN_DURATION') ||
        this.values.get('MAX_DURATION') ||
        this.values.get('DURATION') ||
        this.plannedRepetitionBasedDuration
      );
    },

    get totalDuration(): number {
      return this.plannedDurationMs + (this.nextSet ? this.breakTimeMs : 0);
    },

    get plannedDurationMs(): number {
      return this.plannedDuration * 1000;
    },

    get tons(): number {
      const weight = this.values.get('MIN_WEIGHT') || this.values.get('MAX_WEIGHT') || this.values.get('WEIGHT') || 0;
      const reps = this.values.get('MIN_REPETITIONS') || this.values.get('MAX_REPETITIONS') || this.values.get('REPETITIONS') || 0;
      return (reps * weight) / 1000;
    },

    get index(): number {
      return this.exerciseBlock ? this.exerciseBlock.sets.findIndex((s) => s === this) : -1;
    },

    get isLastSet(): boolean {
      return this.exerciseBlock ? this.index + 1 === this.exerciseBlock.sets.length : false;
    },

    get prevSet(): CoachExerciseBlockSet | undefined {
      if (this.exerciseBlock) {
        const { index } = this;
        return index === 0 ? this.exerciseBlock.prevBlock?.prevSet : this.exerciseBlock.sets[index - 1];
      }
      return undefined;
    },

    get nextSet(): CoachExerciseBlockSet | undefined {
      if (this.exerciseBlock) {
        const { index, isLastSet } = this;
        if (isLastSet) {
          return this.exerciseBlock.nextBlock?.followingSet;
        }
        const nextIndex = index + 1;
        return nextIndex < this.exerciseBlock.sets.length ? this.exerciseBlock.sets[nextIndex] : undefined;
      }
      return undefined;
    },

    get assignedTrackingKeys(): BaseTrackingKey[] {
      return Array.from(this.values.keys())
        .map((item) => (item.startsWith('MIN_') || item.startsWith('MAX_') ? item.substr(4) : item) as BaseTrackingKey)
        .filter((tk) => BasicTrackingKeys.includes(tk))
        .reduce((result, trackingKey) => {
          if (!result.includes(trackingKey)) {
            result.push(trackingKey);
          }
          return result;
        }, [] as BaseTrackingKey[]);
    },

    get assignedTrackingKeysSorted(): BaseTrackingKey[] {
      return this.assignedTrackingKeys.sort((a, b) => TrackingKeysList.indexOf(a) - TrackingKeysList.indexOf(b));
    },

    get unassignedTrackingKeys(): BaseTrackingKey[] {
      const { exerciseBlock, assignedTrackingKeys } = this;
      if (exerciseBlock) {
        return Array.from(new Set(exerciseBlock.trackingParameters.filter((tk) => assignedTrackingKeys.indexOf(tk) < 0)).values());
      }
      return [];
    },

    get unassignedTrackingKeysSorted(): BaseTrackingKey[] {
      return this.unassignedTrackingKeys.sort((a, b) => TrackingKeysList.indexOf(a) - TrackingKeysList.indexOf(b));
    },

    get allTrackingKeysSorted(): BaseTrackingKey[] {
      return this.assignedTrackingKeysSorted.concat(this.unassignedTrackingKeysSorted);
    },

    get unassignedTrackingKeysSortedWithRepetitionsPreference(): BaseTrackingKey[] {
      if (this.unassignedTrackingKeysSorted.includes('REPETITIONS')) {
        return this.unassignedTrackingKeysSorted.filter((tk) => tk !== 'DURATION').filter((tk) => tk !== 'BREAK');
      }
      return this.unassignedTrackingKeysSorted.filter((tk) => tk !== 'BREAK');
    },

    get unassignedTrackingKeysSortedWithDurationPreference(): BaseTrackingKey[] {
      if (this.unassignedTrackingKeysSorted.includes('DURATION')) {
        return this.unassignedTrackingKeysSorted.filter((tk) => tk !== 'REPETITIONS').filter((tk) => tk !== 'BREAK');
      }
      return this.unassignedTrackingKeysSorted.filter((tk) => tk !== 'BREAK');
    },

    get assignedTrackingKeysSortedWithRepetitionsPreference(): BaseTrackingKey[] {
      if (this.assignedTrackingKeysSorted.includes('REPETITIONS')) {
        return this.assignedTrackingKeysSorted.filter((tk) => tk !== 'DURATION').filter((tk) => tk !== 'BREAK');
      }
      return this.assignedTrackingKeysSorted.filter((tk) => tk !== 'BREAK');
    },

    get assignedTrackingKeysSortedWithDurationPreference(): BaseTrackingKey[] {
      if (this.assignedTrackingKeysSorted.includes('DURATION')) {
        return this.assignedTrackingKeysSorted.filter((tk) => tk !== 'REPETITIONS').filter((tk) => tk !== 'BREAK');
      }
      return this.assignedTrackingKeysSorted.filter((tk) => tk !== 'BREAK');
    },

    calculateCalories(bmr24: number): number {
      if (this.plannedCalories > 0) {
        return this.plannedCalories;
      }
      const met = this.exerciseBlock ? this.exerciseBlock.exercise.met || 5.5 : 5.5;
      const timeInHours = Math.max(1, this.plannedDurationMs / 1000.0) / 3600.0;
      return Math.max(1, Math.round(bmr24 * (met || 5.5) * timeInHours)) * 1.2;
    },

    setKey(key: TrackingKey, value?: number): void {
      if (value || value === 0) {
        this.values.set(key, value);
      }
    },

    assignTrackingKey(trackingKey: string, value?: number): void {
      this.values.set(`MIN_${trackingKey}` as PlanableTrackingKey, value || 0);
      this.values.set(`MAX_${trackingKey}` as PlanableTrackingKey, value || 0);
    },

    deleteTrackingKey(trackingKey: string): void {
      this.values.delete(`MIN_${trackingKey}` as PlanableTrackingKey);
      this.values.delete(`MAX_${trackingKey}` as PlanableTrackingKey);
    },

    toWorkoutJS(): any {
      const valuesObj: Record<string, number> = {};
      this.values.forEach((value, key) => {
        valuesObj[key] = value;
      });
      return { values: valuesObj };
    },
  });
}
